import { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/utils';
import type { SuiteAppConfig } from './tokens';

/** Small rounded icon tile showing an app's initials on its accent color. */
function AppIcon({
  label,
  accent,
  className,
}: {
  label: string;
  accent: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-[7px] font-extrabold text-white',
        className,
      )}
      style={{ background: accent }}
    >
      <span className={label.length > 1 ? 'text-[10px] tracking-wide' : 'text-[13px]'}>
        {label}
      </span>
    </div>
  );
}

export interface AppSwitcherProps {
  /** All apps selectable in the switcher (typically Suite Home + real apps). */
  apps: SuiteAppConfig[];
  /** The currently active app id. */
  currentAppId: string;
  /** Called when the user picks an app. */
  onSelect: (appId: string) => void;
}

/**
 * The sidebar app switcher: a button showing the current app, opening a
 * dropdown of all suite apps. Fully controlled — the parent owns which app is
 * active and how selection maps to navigation.
 */
export function AppSwitcher({ apps, currentAppId, onSelect }: AppSwitcherProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const current = apps.find((a) => a.id === currentAppId) ?? apps[0];

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [open]);

  if (!current) return null;

  return (
    <div ref={rootRef} className="relative mx-3 mb-1 mt-2.5">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2.5 rounded-[10px] border border-slate-200 bg-white p-2.5 text-left transition-[border-color,box-shadow] hover:border-slate-300 hover:shadow-[0_1px_3px_rgb(0_0_0/0.06)]"
      >
        <AppIcon label={current.iconLabel} accent={current.accent} className="h-[26px] w-[26px]" />
        <span className="min-w-0 flex-1">
          <small className="block text-[10.5px] uppercase tracking-wide text-slate-400">
            Current app
          </small>
          <b className="block truncate text-[13px] font-semibold text-slate-800">{current.name}</b>
        </span>
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#94a3b8"
          strokeWidth="2"
          aria-hidden
          className="shrink-0"
        >
          <path d="m7 15 5 5 5-5M7 9l5-5 5 5" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 rounded-xl border border-slate-200 bg-white p-1.5 shadow-[0_12px_24px_-6px_rgb(0_0_0/0.15)]"
        >
          {apps.map((app) => {
            const active = app.id === currentAppId;
            return (
              <button
                key={app.id}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onSelect(app.id);
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-lg p-2 text-left hover:bg-slate-50',
                  active && 'bg-[color-mix(in_srgb,var(--accent)_5%,white)]',
                )}
              >
                <AppIcon label={app.iconLabel} accent={app.accent} className="h-[26px] w-[26px]" />
                <span className="min-w-0">
                  <b className="block text-[13px] font-semibold text-slate-800">{app.name}</b>
                  {app.subtitle && (
                    <small className="block text-[11px] text-slate-500">{app.subtitle}</small>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
